import { createContext, useContext } from "react";
import { createStore, useStore } from "zustand";
import { immer } from "zustand/middleware/immer";
import { useShallow } from "zustand/shallow";

import {
  normalizeAuthoringDraft,
  type TextComposerAuthoringState,
} from "./authoringState";
import type { TextComposerExtension } from "./extensionContract";
import {
  assertNoOwnedKeyCollisions,
  resolveExtensionOrder,
} from "./extensionRegistry";
import { defaultExtensions } from "../extensions/defaultExtensions";

export interface MessageDraftMedia {
  name: string;
  mimeType: string;
  sizeBytes: number;
  cdnUrl: string;
}

export interface CursorPosition {
  start: number;
  end: number;
}

export interface MessageDraftState {
  authoringState: TextComposerAuthoringState;
  initialAuthoringState: TextComposerAuthoringState;
  cursorPosition: CursorPosition | null;
  previewRecipientViewMode: "primary" | "fallback";
  dragonTileCategory: string | undefined;
}

export type MessageDraftActions = {
  setBodyText: (bodyText: string) => void;
  insertAtCursor: (text: string) => void;
  setCursorPosition: (start: number, end: number) => void;
  setPreviewRecipientViewMode: (mode: "primary" | "fallback") => void;
} & Record<string, (...args: never[]) => void>;

export interface MessageDraftStore extends MessageDraftState {
  actions: MessageDraftActions;
}

export interface CreateMessageDraftStoreOptions {
  dragonTileCategory?: string;
  extensions?: TextComposerExtension[];
  /**
   * Initial channel-preview toggle. Defaults to "primary" (e.g. RCS). Pass
   * "fallback" to open on the SMS/MMS side — e.g. when the site only supports
   * SMS. Consumers decide the policy; the composer just honors the seed.
   */
  initialPreviewRecipientViewMode?: "primary" | "fallback";
}

export function createMessageDraftStore(
  initialState: TextComposerAuthoringState = {},
  options?: CreateMessageDraftStoreOptions,
) {
  const inputExtensions = options?.extensions ?? defaultExtensions;
  assertNoOwnedKeyCollisions(inputExtensions);
  const extensions = resolveExtensionOrder(inputExtensions);

  return createStore<MessageDraftStore>()(
    immer((set, get) => {
      const extensionActions: Record<string, (...args: never[]) => void> = {};
      for (const ext of extensions) {
        if (ext.createActions) {
          const actions = ext.createActions(
            (fn) =>
              set((state) => {
                fn(state);
              }),
            () => get(),
          );
          Object.assign(extensionActions, actions);
        }
      }

      const actions: MessageDraftActions = {
        setBodyText(bodyText: string) {
          set((state) => {
            if (bodyText.trim().length === 0) {
              delete state.authoringState.bodyText;
            } else {
              state.authoringState.bodyText = bodyText;
            }
          });
        },
        insertAtCursor(text: string) {
          set((state) => {
            const currentText = state.authoringState.bodyText ?? "";
            const cursor = state.cursorPosition;
            const start = cursor?.start ?? currentText.length;
            const end = cursor?.end ?? start;
            const nextText =
              currentText.slice(0, start) + text + currentText.slice(end);
            const nextCursorPos = start + text.length;

            if (nextText.trim().length === 0) {
              delete state.authoringState.bodyText;
            } else {
              state.authoringState.bodyText = nextText;
            }
            state.cursorPosition = {
              start: nextCursorPos,
              end: nextCursorPos,
            };
          });
        },
        setCursorPosition(start: number, end: number) {
          set((state) => {
            state.cursorPosition = { start, end };
          });
        },
        setPreviewRecipientViewMode(mode: "primary" | "fallback") {
          set((state) => {
            state.previewRecipientViewMode = mode;
          });
        },
        ...extensionActions,
      };

      const normalizedInitial = normalizeAuthoringDraft(
        initialState,
        extensions,
      );

      return {
        authoringState: normalizedInitial,
        initialAuthoringState: normalizedInitial,
        cursorPosition: null,
        previewRecipientViewMode:
          options?.initialPreviewRecipientViewMode ?? "primary",
        dragonTileCategory: options?.dragonTileCategory,
        actions,
      };
    }),
  );
}

export type MessageDraftStoreApi = ReturnType<typeof createMessageDraftStore>;

// --- Context ---

export interface MessageDraftContextValue {
  store: MessageDraftStoreApi;
  extensions: TextComposerExtension[];
  storeId?: string;
}

export const MessageDraftStoreContext =
  createContext<MessageDraftContextValue | null>(null);

// --- Hooks ---

function useMessageDraftContext(): MessageDraftContextValue {
  const ctx = useContext(MessageDraftStoreContext);
  if (!ctx) {
    throw new Error(
      "useMessageDraftStore must be used within a MessageDraftProvider",
    );
  }
  return ctx;
}

export function useMessageDraftStore<T>(
  selector: (state: MessageDraftStore) => T,
): T {
  const { store } = useMessageDraftContext();
  return useStore(store, selector);
}

export function useMessageDraftStoreShallow<T>(
  selector: (state: MessageDraftStore) => T,
): T {
  const { store } = useMessageDraftContext();
  return useStore(store, useShallow(selector));
}

export function useMessageDraftExtensions(): TextComposerExtension[] {
  return useMessageDraftContext().extensions;
}

export function useMessageDraftStoreId(): string {
  return useMessageDraftContext().storeId ?? "single";
}
